import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Page } from '../../components/Layout';
import { Sheet } from '../../components/Sheet';
import { QueryView } from '../../components/states';
import { Avatar, Button, Card, LevelBadge, SectionTitle, Stat } from '../../components/ui';
import { useClub, useUserSearch } from '../../hooks/queries';
import { MY_COLOR } from '../../lib/colors';
import { formatDistance, formatNumber } from '../../lib/format';
import { useAuth } from '../../stores/auth';
import { toast } from '../../stores/toasts';
import { ClubBadge } from './ClubBadge';
import { ClubFormSheet } from './ClubFormSheet';
import { clubApi, useClubAction } from './useClubActions';
import { useT } from '../../i18n';

export function ClubDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useClub(id);
  const me = useAuth((s) => s.user?.id);
  const navigate = useNavigate();
  const [inviting, setInviting] = useState(false);
  const [manage, setManage] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const t = useT();

  const join = useClubAction(() => clubApi.requestJoin(id!), 'Request sent — the owner will review it');
  const cancel = useClubAction((rid: string) => clubApi.cancelRequest(rid), 'Request cancelled');
  const approve = useClubAction((rid: string) => clubApi.approve(rid), 'Member approved');
  const decline = useClubAction((rid: string) => clubApi.decline(rid));
  const leave = useClubAction(() => clubApi.leave(), 'You left the club');
  const remove = useClubAction((uid: string) => clubApi.remove(id!, uid), 'Member removed');
  const transfer = useClubAction((uid: string) => clubApi.transfer(id!, uid), 'Ownership transferred');
  const reset = useClubAction(() => clubApi.resetCode(id!), 'New invite link created');

  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        {t('← Back')}
      </button>
      <QueryView query={q}>
        {(c) => {
          const isOwner = c.myRole === 'OWNER';
          const link = c.inviteCode ? `${location.origin}/join/${c.inviteCode}` : null;
          const d = formatDistance(c.stats.totalDistanceM);
          const share = async () => {
            if (!link) return;
            try {
              if (navigator.share) await navigator.share({ title: t('Join {name} on Runner Wars', { name: c.name }), url: link });
              else {
                await navigator.clipboard.writeText(link);
                toast({ kind: 'success', title: t('Invite link copied') });
              }
            } catch {
              /* user cancelled share */
            }
          };
          return (
            <div data-testid="club-detail">
              <div className="flex flex-col items-center text-center">
                <ClubBadge club={c} size="lg" link={false} />
                <h1 className="mt-3 font-display text-3xl font-bold" data-testid="club-name">
                  {c.name}
                </h1>
                {c.description ? <p className="mt-1 max-w-sm text-sm text-ink-300">{c.description}</p> : null}
                <p className="mt-1 text-xs text-ink-500">{t('Owner')} · {c.owner.displayName}</p>
                {isOwner ? (
                  <Button variant="secondary" size="sm" className="mt-3" onClick={() => setEditing(true)} data-testid="edit-club">
                    {t('Edit club')}
                  </Button>
                ) : null}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <Stat label={t('Members')} value={c.stats.members} accent="sky" testId="club-members" />
                <Stat label={t('Territory')} value={formatNumber(c.stats.territories)} accent="volt" />
                <Stat label={t('Club XP')} value={formatNumber(c.stats.totalXp)} accent="gold" />
                <Stat label={t('Distance')} value={d.value} unit={d.unit} />
              </div>

              <div className="mt-4">
                {c.myRole ? (
                  <div className="grid grid-cols-2 gap-3">
                    {isOwner ? (
                      <Button onClick={() => setInviting(true)} data-testid="invite-runners">
                        {t('Invite runners')}
                      </Button>
                    ) : null}
                    <Button variant="secondary" onClick={share} className={isOwner ? '' : 'col-span-2'} data-testid="share-invite">
                      {t('Share invite link')}
                    </Button>
                  </div>
                ) : c.myRequest?.type === 'JOIN_REQUEST' ? (
                  <Button variant="secondary" className="w-full" onClick={() => cancel.mutate(c.myRequest!.id)} data-testid="cancel-request">
                    {t('Request pending · Cancel')}
                  </Button>
                ) : (
                  <Button className="w-full" onClick={() => join.mutate(undefined)} loading={join.isPending} data-testid="request-join">
                    {t(c.myRequest?.type === 'INVITE' ? 'Accept invitation' : 'Request to join')}
                  </Button>
                )}
              </div>

              {isOwner && c.pendingRequests.filter((r) => r.type === 'JOIN_REQUEST').length ? (
                <>
                  <SectionTitle>{t('Join requests')}</SectionTitle>
                  <div className="space-y-2" data-testid="join-requests">
                    {c.pendingRequests
                      .filter((r) => r.type === 'JOIN_REQUEST')
                      .map((r) => (
                        <Card key={r.id} className="flex items-center gap-3">
                          <Avatar name={r.user.displayName} color={r.user.color} size="sm" src={r.user.avatarUrl} />
                          <Link to={`/players/${r.user.id}`} className="flex-1 font-semibold">
                            {r.user.displayName}
                          </Link>
                          <Button size="sm" onClick={() => approve.mutate(r.id)} disabled={approve.isPending} data-testid="approve-request">
                            {t('Approve')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => decline.mutate(r.id)}>
                            ✕
                          </Button>
                        </Card>
                      ))}
                  </div>
                </>
              ) : null}

              {isOwner && c.pendingRequests.some((r) => r.type === 'INVITE') ? (
                <p className="mt-3 text-xs text-ink-400">
                  {t('Invited, waiting for answer:')} {c.pendingRequests.filter((r) => r.type === 'INVITE').map((r) => r.user.displayName).join(', ')}
                </p>
              ) : null}

              <SectionTitle>{t('Members')}</SectionTitle>
              <ul className="divide-y divide-ink-800 overflow-hidden rounded-2xl border border-ink-700/80 bg-ink-850" data-testid="club-member-list">
                {c.members.map((m) => (
                  <li key={m.user.id} className="flex items-center gap-3 px-4 py-3">
                    <Avatar name={m.user.displayName} color={m.user.id === me ? MY_COLOR : m.user.color} size="sm" src={m.user.avatarUrl} />
                    <Link to={m.user.id === me ? '/profile' : `/players/${m.user.id}`} className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">
                        {m.user.displayName}
                        {m.user.id === me ? ` ${t('(you)')}` : ''}
                      </span>
                      <span className="text-xs text-ink-500">
                        {m.role === 'OWNER' ? `${t('Owner')} · ` : ''}⬢ {m.territoriesOwned} · {formatNumber(m.totalXp)} XP
                      </span>
                    </Link>
                    <LevelBadge level={m.level} size="sm" />
                    {isOwner && m.user.id !== me ? (
                      <button className="px-1 text-ink-400" onClick={() => setManage(m.user.id)} aria-label={t('Manage {name}', { name: m.user.displayName })}>
                        ⋯
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>

              {c.myRole ? (
                <div className="mt-6 space-y-2">
                  {isOwner && link ? (
                    <Button variant="ghost" className="w-full" onClick={() => reset.mutate(undefined)}>
                      {t('Reset invite link')}
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    className="w-full text-enemy"
                    onClick={() => {
                      if (isOwner && c.stats.members > 1) {
                        toast({ kind: 'info', title: t('Transfer ownership first'), body: t('Tap ⋯ next to a member to make them owner.') });
                        return;
                      }
                      leave.mutate(undefined, { onSuccess: () => navigate('/clubs', { replace: true }) });
                    }}
                    data-testid="leave-club"
                  >
                    {t(isOwner && c.stats.members === 1 ? 'Disband club' : 'Leave club')}
                  </Button>
                </div>
              ) : null}

              <ClubFormSheet open={editing} onClose={() => setEditing(false)} club={c} />
              <InviteSheet open={inviting} onClose={() => setInviting(false)} clubId={c.id} memberIds={new Set(c.members.map((m) => m.user.id))} />
              <Sheet open={!!manage} onClose={() => setManage(null)} label={t('Manage member')}>
                <h3 className="font-display text-xl font-bold uppercase">{c.members.find((m) => m.user.id === manage)?.user.displayName}</h3>
                <div className="mt-4 grid gap-3">
                  <Button variant="secondary" onClick={() => manage && transfer.mutate(manage, { onSuccess: () => setManage(null) })}>
                    {t('Make owner')}
                  </Button>
                  <Button variant="danger" onClick={() => manage && remove.mutate(manage, { onSuccess: () => setManage(null) })}>
                    {t('Remove from club')}
                  </Button>
                </div>
              </Sheet>
            </div>
          );
        }}
      </QueryView>
    </Page>
  );
}

function InviteSheet({ open, onClose, clubId, memberIds }: { open: boolean; onClose: () => void; clubId: string; memberIds: Set<string> }) {
  const [q, setQ] = useState('');
  const users = useUserSearch(q);
  const [sent, setSent] = useState<Set<string>>(new Set());
  const t = useT();
  const invite = useClubAction((uid: string) => clubApi.invite(clubId, uid), 'Invitation sent');
  return (
    <Sheet open={open} onClose={onClose} label={t('Invite runners')}>
      <h3 className="font-display text-2xl font-bold uppercase">{t('Invite runners')}</h3>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('Search runners by name')}
        className="mt-3 h-12 w-full rounded-xl border border-ink-700 bg-ink-900 px-4 outline-none focus:border-volt"
        data-testid="user-search"
        aria-label={t('Search runners')}
      />
      <ul className="mt-3 space-y-2">
        {q.trim().length < 2 ? (
          <li className="py-4 text-center text-sm text-ink-500">{t('Type at least 2 letters')}</li>
        ) : users.data?.length === 0 ? (
          <li className="py-4 text-center text-sm text-ink-500">{t('No runners found')}</li>
        ) : (
          users.data?.map((u) => (
            <li key={u.id} className="flex items-center gap-3 rounded-xl bg-ink-800 p-2.5">
              <Avatar name={u.displayName} color={u.color} size="sm" src={u.avatarUrl} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{u.displayName}</div>
                <div className="text-xs text-ink-500">{t('Level {n}', { n: u.level })}{u.club ? ` · ${u.club.name}` : ''}</div>
              </div>
              {memberIds.has(u.id) ? (
                <span className="text-xs text-ink-400">{t('Member')}</span>
              ) : u.club ? (
                <span className="text-xs text-ink-500">{t('In a club')}</span>
              ) : sent.has(u.id) ? (
                <span className="text-xs font-semibold text-volt">{t('Invited')}</span>
              ) : (
                <Button size="sm" onClick={() => invite.mutate(u.id, { onSuccess: () => setSent(new Set(sent).add(u.id)) })} data-testid={`invite-${u.displayName}`}>
                  {t('Invite')}
                </Button>
              )}
            </li>
          ))
        )}
      </ul>
    </Sheet>
  );
}
